import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { EndPoint, InitCache, Session, ImpersonationState, ClientOverride, WebCacheTS, KioskCacheItem, LocationCacheItem, ProductCacheItem } from "../types/session.ts";
import { setSessionAccessor, setOnSessionExpired, setImpersonationAccessor, servicePost, servicePostWithSession, bumpEndpointEpoch, isDroppedCallError } from "../api/client.ts";
import {
  setClientTimeZoneAccessor,
  setClientLocaleAccessor,
  getBrowserTimeZone,
  getBrowserLocale,
} from "../util/clientEnv.ts";
import { setServerTimeZoneAccessor, setServerUtcOffsetAccessor } from "../util/serverTime.ts";
import { buildKioskZoneResolver, setKioskZoneResolver } from "../util/kioskZone.ts";
import { recordCacheMergeEvent } from "../debug/cacheDebug.ts";
import { mergeCacheDelta, parseMigratedFilters } from "./cacheMerge.ts";
import {
  clearPersistedSession,
  loadPersistedSession,
  persistSession,
  persistCache,
  loadPersistedCache,
  clearPersistedCache,
  persistActiveEndpoint,
  loadPersistedActiveEndpoint,
  clearPersistedActiveEndpoint,
  isCacheShapeValid,
  recordCacheClearAttempt,
  resetCacheClearAttempts,
  consumeSessionBridge,
  MAX_AUTO_CACHE_CLEARS,
} from "../api/auth.ts";
import { pushRecentEndpointId } from "./recentEndpoints.ts";
import { clearTokens as clearCockpitTokens } from "./cockpitJwt.ts";
import { useToast } from "../hooks/useToast.ts";

export type AppPhase = "validating" | "signed-out" | "picking-endpoint" | "loading-cache" | "ready";

interface AuthContextValue {
  session: Session | null;
  cache: InitCache | null;
  loading: boolean;
  phase: AppPhase;
  /** User's endpoints after IP filtering. Fetched on login / endpoint switch / IP-override refresh. Not persisted. */
  endpoints: EndPoint[];
  /** The endpoint matching session.ActiveEndPointID, or null. Most pages only need this; prefer it over scanning endpoints. */
  activeEndpoint: EndPoint | null;
  /** Re-fetch endpoints from backend (applies FilterIPRestriction — and DEBUG IP override if active). */
  fetchEndpoints(): Promise<EndPoint[]>;
  login(session: Session): Promise<void>;
  logout(): void;
  setActiveEndpoint(id: number): void;
  switchEndpoint(): void;
  refreshCache(): Promise<void>;
  clearCache(): Promise<void>;
  /** Splice a freshly-saved row into `cache.CommonData.Common` and persist.
   *  Use after `WebSaveValuesCB` returns success — sidesteps a full
   *  `refreshCache()` round-trip. */
  appendCommonValue(item: unknown): void;
  /** Upsert a kiosk row in `cache.CommonData.Kiosk` (replace by KioskID, else
   *  append) and persist. Use after `WebSaveKiosk` returns success so dropdowns
   *  fed from CommonData see the new row before the next cache delta. */
  upsertKiosk(item: KioskCacheItem): void;
  /** Upsert a location row in `cache.CommonData.Location` (replace by
   *  LocationID, else append) and persist. */
  upsertLocation(item: LocationCacheItem): void;
  /** Upsert a promotion row in `cache.Promotions` (replace by PromotionID,
   *  else append) and persist. */
  upsertPromotion(item: unknown): void;
  /** Upsert a product row in `cache.CommonData.Product` (replace by ProductID,
   *  else append) and persist. */
  upsertProduct(item: ProductCacheItem): void;
  /** Upsert an ingress-group row in `cache.CommonData.IngressGroup` (replace
   *  by ID, else append) and persist. */
  upsertIngressGroup(item: { ID: number; [k: string]: unknown }): void;
  /** Debug-only: refetch WebInitCache with a caller-supplied TS. Pass "full" to force a full fetch (TS: undefined). */
  debugRefetchWithTs(tsOverride: WebCacheTS | "full" | undefined): Promise<void>;
  canRead(pageRowId: number): boolean;
  canWrite(pageRowId: number): boolean;
  /** Current impersonation state (null = not impersonating). */
  impersonation: ImpersonationState | null;
  /** Set or clear impersonation. Triggers cache reload. */
  setImpersonation(state: ImpersonationState | null): void;
  /** DEBUG: re-fetch endpoint list — server filters using current OverrideIPAddress. */
  refreshEndpointsForIPImpersonation(): Promise<void>;
  /** Always the real user's SuperAdmin flag, unaffected by impersonation. Use only for SA tooling access. */
  realSuperAdmin: boolean;
  /** Real user's SuperUserTools flag (subset of SA tooling). Carried on the session (JWT claim or login DTO). */
  realSuperUserTools: boolean;
  /** Effective SuperAdmin flag respecting impersonation. Use for all non-SA-tooling checks. */
  isSuperAdmin: boolean;
  /** The user the server acts as — the impersonated user under user-mode impersonation, otherwise
   *  the signed-in one. Use for "is this me / mine" data checks; session.UID stays the real user
   *  for per-browser preferences and storage keys. */
  effectiveUserId: number;
  /** SA-only override of perceived browser timezone + locale (in-memory).
   *  Read effective values via util/clientEnv.ts hooks/accessors — those
   *  pick up this state automatically. */
  clientOverride: ClientOverride | null;
  setClientOverride(state: ClientOverride | null): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Log a rejection unless it's a call we dropped on purpose (endpoint switch abort,
 *  suppressed no-endpoint call). Keeps the console meaningful now that switching
 *  endpoints cancels everything in flight. */
function logUnlessDropped(err: unknown): void {
  if (!isDroppedCallError(err)) console.error(err);
}

/** The persisted cache is the real user's own. It outlives a reload and impersonation does not, so
 *  an impersonated snapshot written here would come back as the admin's cache after a reload, and the
 *  delta fetch on top of it would never correct it. Impersonated caches stay in memory only. */
function persistUnlessImpersonating(imp: ImpersonationState | null, uid: number, endpointId: number, cache: InitCache): void {
  if (imp) return;
  persistCache(uid, endpointId, cache);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  const [session, setSession] = useState<Session | null>(null);
  const [cache, setCache] = useState<InitCache | null>(null);
  const [loading, setLoading] = useState(true);
  const cacheRef = useRef<InitCache | null>(null);
  const sessionRef = useRef<Session | null>(null);
  // Shared in-flight WebGetUserEndpoints promise — dedupes the bootstrap effect
  // against StrictMode's double-mount and against EndpointSelectionPage's own
  // refresh. Cleared on every endpoint change so a switch never adopts a promise
  // that was aborted with the previous epoch.
  const endpointsBootRef = useRef<Promise<unknown> | null>(null);
  const [endpoints, setEndpoints] = useState<EndPoint[]>([]);
  const activeEndpoint = useMemo(
    () => endpoints.find((e) => e.ID === session?.ActiveEndPointID) ?? null,
    [endpoints, session?.ActiveEndPointID],
  );
  const [impersonation, setImpersonationState] = useState<ImpersonationState | null>(null);
  const impersonationRef = useRef<ImpersonationState | null>(null);
  const [clientOverride, setClientOverrideState] = useState<ClientOverride | null>(null);
  const clientOverrideRef = useRef<ClientOverride | null>(null);
  const realSuperAdmin = session?.SuperAdmin === true;
  const realSuperUserTools = session?.SuperUserTools === true;
  const cacheSuperAdmin = cache?.SuperAdmin;
  // The cache's UserInfo is built for the server's effective user (WebInitCache), so it follows
  // user-mode impersonation; the session is always the real one.
  const effectiveUserId = cache?.UserInfo?.UserID ?? session?.UID ?? 0;
  const isSuperAdmin = useMemo(() => {
    const imp = impersonationRef.current;
    // While impersonating, the cache carries the server's effective flag (the impersonated
    // user's own, or the select-mode override) — the one every server gate reads, so
    // super-admin-only UI disappears exactly when the server would refuse it. It can only
    // narrow: impersonation never grants super admin.
    if (imp && typeof cacheSuperAdmin === "boolean") return realSuperAdmin && cacheSuperAdmin;
    // Cache not loaded yet (it is dropped on every impersonation change), or an older server.
    if (imp?.mode === "select" && imp.overrideSuperAdmin != null) return realSuperAdmin && imp.overrideSuperAdmin;
    return realSuperAdmin;
  }, [realSuperAdmin, impersonation, cacheSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cacheRef.current = cache; }, [cache]);
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Always write ref synchronously alongside state so same-tick fetches see the new session.
  const applySession = useCallback((next: Session | null) => {
    sessionRef.current = next;
    setSession(next);
  }, []);

  const phase = useMemo<AppPhase>(() => {
    if (loading) return "validating";
    if (!session) return "signed-out";
    if (!session.ActiveEndPointID) return "picking-endpoint";
    if (!cache) return "loading-cache";
    return "ready";
  }, [loading, session, cache]);

  const fetchInitCache = useCallback(async (s: Session, signal?: AbortSignal, tsOverride?: WebCacheTS | "full" | undefined) => {
    const ts = tsOverride === "full"
      ? undefined
      : (tsOverride !== undefined ? tsOverride : cacheRef.current?.TS);
    const requestedTs: WebCacheTS | undefined | "full" = tsOverride === "full" ? "full" : ts;
    const result = await servicePostWithSession<InitCache>(s, "WebInitCache", { TS: ts }, signal);
    if (!result || result.bError || !result.oData) {
      recordCacheMergeEvent({
        requestTs: requestedTs,
        prev: cacheRef.current,
        response: undefined,
        merged: null,
        mergeOk: false,
        error: result?.sMessage ?? "no response",
      });
      return;
    }
    try {
      let mergedOut: InitCache | null = null;
      const prevForLog = cacheRef.current;
      setCache(prev => {
        const merged = mergeCacheDelta(prev, result.oData);
        if (!isCacheShapeValid(merged)) throw new Error("cache shape invalid after merge");
        mergedOut = merged;
        if (s.UID && s.ActiveEndPointID) persistUnlessImpersonating(impersonationRef.current, s.UID, s.ActiveEndPointID, merged);
        return merged;
      });
      if (mergedOut) resetCacheClearAttempts();
      recordCacheMergeEvent({
        requestTs: requestedTs,
        prev: prevForLog,
        response: result.oData,
        merged: mergedOut,
        mergeOk: true,
      });
    } catch (err) {
      console.error("cache merge/validate failed — auto-recovering:", err);
      recordCacheMergeEvent({
        requestTs: requestedTs,
        prev: cacheRef.current,
        response: result.oData,
        merged: null,
        mergeOk: false,
        error: String(err),
      });
      // Drop local cache so the retry below asks the server for a full payload (no delta TS).
      clearPersistedCache(s.UID, s.ActiveEndPointID);
      cacheRef.current = null;
      setCache(null);
      const attempts = recordCacheClearAttempt();
      if (attempts > MAX_AUTO_CACHE_CLEARS) {
        toast("Cache could not be loaded — try signing out and back in.", "error");
        return;
      }
      try {
        const full = await servicePostWithSession<InitCache>(s, "WebInitCache", { TS: undefined }, signal);
        if (full && !full.bError && full.oData && isCacheShapeValid(full.oData)) {
          parseMigratedFilters(full.oData);
          cacheRef.current = full.oData;
          setCache(full.oData);
          if (s.UID && s.ActiveEndPointID) persistUnlessImpersonating(impersonationRef.current, s.UID, s.ActiveEndPointID, full.oData);
          resetCacheClearAttempts();
          recordCacheMergeEvent({
            requestTs: "full",
            prev: null,
            response: full.oData,
            merged: full.oData,
            mergeOk: true,
          });
        } else {
          toast("Cache refresh failed — server returned invalid data.", "error");
          recordCacheMergeEvent({
            requestTs: "full",
            prev: null,
            response: full?.oData,
            merged: null,
            mergeOk: false,
            error: full?.sMessage ?? "invalid full response",
          });
        }
      } catch (retryErr) {
        console.error("full cache refetch failed:", retryErr);
        toast("Cache refresh failed. Check network and try again.", "error");
        recordCacheMergeEvent({
          requestTs: "full",
          prev: null,
          response: undefined,
          merged: null,
          mergeOk: false,
          error: String(retryErr),
        });
      }
    }
  }, [toast]);

  const debugRefetchWithTs = useCallback(async (tsOverride: WebCacheTS | "full" | undefined) => {
    const s = sessionRef.current;
    if (!s) return;
    await fetchInitCache(s, undefined, tsOverride);
  }, [fetchInitCache]);

  // Wire up session accessor, impersonation accessor, and expiry handler
  // Register once — accessor reads sessionRef so it's always live, even for
  // fetches fired synchronously right after setSession (before the [session]
  // effect would have run).
  useEffect(() => {
    setSessionAccessor(() => sessionRef.current);
  }, []);

  useEffect(() => {
    impersonationRef.current = impersonation;
    setImpersonationAccessor(() => impersonationRef.current);
  }, [impersonation]);

  // Publish override-aware accessors so non-React code (cellRenderer module
  // helpers, api/endpoints, chart utils) reads live values without prop-drilling.
  useEffect(() => {
    clientOverrideRef.current = clientOverride;
    setClientTimeZoneAccessor(() => clientOverrideRef.current?.timeZone || getBrowserTimeZone());
    setClientLocaleAccessor(()   => clientOverrideRef.current?.locale   || getBrowserLocale());
  }, [clientOverride]);

  // The database's own clock, needed to resolve the zoneless timestamps the backend
  // sends. Production runs UTC and dev boxes run CET/CEST, so neither can be a
  // constant. The zone id is preferred where available — the offset alone is a
  // snapshot and misconverts values from the other half of the DST year. InitCache
  // carries the offset in HOURS; serverTime wants minutes.
  useEffect(() => {
    setServerUtcOffsetAccessor(() => (cacheRef.current?.ServerTimezoneOffset ?? 0) * 60);
    setServerTimeZoneAccessor(() => cacheRef.current?.ServerTimezoneIana || null);
  }, []);

  // Kiosk* grid cells convert into the kiosk's own zone, resolved kiosk → location →
  // TimeZoneIana. Rebuilt when the cache changes so the lookup maps are built once
  // rather than per cell.
  useEffect(() => {
    setKioskZoneResolver(buildKioskZoneResolver(
      cache?.CommonData?.Kiosk as Array<{ KioskID: number; LocationID: number }> | undefined,
      cache?.CommonData?.Location as Array<{ LocationID: number; TimeZone?: string; TimeZoneIana?: string }> | undefined,
      cache?.TimeZones,
    ));
  }, [cache]);

  const setClientOverride = useCallback((state: ClientOverride | null) => {
    setClientOverrideState(state);
    clientOverrideRef.current = state;
  }, []);

  useEffect(() => {
    setOnSessionExpired((reason) => {
      // Stash the reason so SignInPage can render a banner explaining why we just
      // bounced the user out. sessionStorage is tab-scoped, which is what we want —
      // a fresh tab shouldn't see a stale "your session ended" message.
      try { sessionStorage.setItem("wac_session_ended_reason", reason); } catch { /* ignore */ }
      clearPersistedSession();
      clearCockpitTokens();
      sessionRef.current = null;
      setSession(null);
      setCache(null);
      // Kill anything still running under the dead session so its continuations
      // can't fire follow-up calls against a session that no longer exists.
      endpointsBootRef.current = null;
      bumpEndpointEpoch();
    });
  }, []);

  // Load persisted session + cache on mount, then fetch delta from server
  // AbortController cancels the in-flight request on StrictMode's unmount-remount.
  useEffect(() => {
    const ac = new AbortController();
    // Cross-tab bridge: a `#wac_session=…` fragment writes the session into
    // sessionStorage before we read it, so target=_blank links from another
    // tab don't bounce through sign-in.
    consumeSessionBridge();
    const persisted = loadPersistedSession();
    if (persisted) {
      applySession(persisted);
      // Restore last-known active endpoint immediately so UI (breadcrumb, nav) has
      // Title/ModuleID/PartnerID/CustomerID before the full-list fetch returns.
      const storedActive = loadPersistedActiveEndpoint(persisted.UID);
      if (storedActive && storedActive.ID === persisted.ActiveEndPointID) {
        setEndpoints([storedActive]);
      }
      // Fetch fresh list to reflect current IP-restriction state.
      if (!endpointsBootRef.current) {
        endpointsBootRef.current = servicePost<EndPoint[]>("WebGetUserEndpoints")
          .finally(() => { endpointsBootRef.current = null; });
      }
      (endpointsBootRef.current as Promise<ReturnType<typeof servicePost<EndPoint[]>> extends Promise<infer R> ? R : never>)
        .then((r) => {
          if (ac.signal.aborted) return;
          if (r && !r.bError && Array.isArray(r.oData)) {
            setEndpoints(r.oData);
            const active = r.oData.find(e => e.ID === persisted.ActiveEndPointID) ?? null;
            persistActiveEndpoint(persisted.UID, active);
          }
        })
        .catch((err) => { if (!ac.signal.aborted) logUnlessDropped(err); });
      if (persisted.ActiveEndPointID) {
        // Restore cached data immediately so the UI can render while the delta loads
        const stored = loadPersistedCache(persisted.UID, persisted.ActiveEndPointID) as InitCache | null;
        if (stored && isCacheShapeValid(stored)) {
          parseMigratedFilters(stored);
          cacheRef.current = stored;
          setCache(stored);
        } else if (stored) {
          // Structural drift (old build, half-written blob, schema change). Drop it; the
          // fetch below will pull a full payload (no TS) since cacheRef stays null.
          console.warn("discarding corrupt persisted cache");
          clearPersistedCache(persisted.UID, persisted.ActiveEndPointID);
        }
        // Fetch delta (TS from stored cache will limit the response to changes only)
        fetchInitCache(persisted, ac.signal)
          .catch((err) => { if (!ac.signal.aborted) logUnlessDropped(err); })
          .finally(() => { if (!ac.signal.aborted) setLoading(false); });
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
    return () => ac.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEndpoints = useCallback(async (): Promise<EndPoint[]> => {
    try {
      // Dedupe with the bootstrap effect — both paths hit WebGetUserEndpoints;
      // share the in-flight Promise so StrictMode double-mount or an
      // EndpointSelectionPage refresh during boot doesn't fire it twice.
      if (!endpointsBootRef.current) {
        endpointsBootRef.current = servicePost<EndPoint[]>("WebGetUserEndpoints")
          .finally(() => { endpointsBootRef.current = null; });
      }
      const result = (await endpointsBootRef.current) as Awaited<ReturnType<typeof servicePost<EndPoint[]>>>;
      if (result && !result.bError && Array.isArray(result.oData)) {
        setEndpoints(result.oData);
        const s = sessionRef.current;
        if (s?.UID) {
          const active = result.oData.find(e => e.ID === s.ActiveEndPointID) ?? null;
          persistActiveEndpoint(s.UID, active);
        }
        return result.oData;
      }
    } catch (err) {
      // A dropped call (endpoint switch aborted it) must not blank the picker —
      // the switch itself queues a fresh fetch under the new epoch.
      if (isDroppedCallError(err)) return [];
      console.error("fetchEndpoints failed:", err);
    }
    setEndpoints([]);
    return [];
  }, []);

  const login = useCallback(
    async (newSession: Session) => {
      persistSession(newSession);
      applySession(newSession);
      // Fetch endpoints fresh from the server — never carried in the persisted session.
      const eps = await fetchEndpoints();
      // Auto-select if exactly one endpoint
      if (eps.length === 1 && !newSession.ActiveEndPointID) {
        newSession = { ...newSession, ActiveEndPointID: eps[0]!.ID };
        persistSession(newSession);
        applySession(newSession);
      }
      // DEBUG-only: if a sign-in IP override was used, lift it into the live impersonation
      // context so subsequent service calls also send it. Only meaningful for super-admins.
      if (__DEV_FEATURES__ && newSession.SuperAdmin) {
        const { getSignInOverrideIP, clearSignInOverrideIP } = await import("./signInOverride.ts");
        const ip = getSignInOverrideIP();
        if (ip) {
          const impState: ImpersonationState = { mode: "select", overrideIPAddress: ip };
          setImpersonationState(impState);
          impersonationRef.current = impState;
          clearSignInOverrideIP();
        }
      }
      if (newSession.ActiveEndPointID) {
        await fetchInitCache(newSession);
      }
    },
    [fetchInitCache, fetchEndpoints]
  );

  const logout = useCallback(() => {
    clearPersistedSession();
    clearCockpitTokens();
    clearPersistedCache();
    clearPersistedActiveEndpoint();
    applySession(null);
    cacheRef.current = null;
    setCache(null);
    setEndpoints([]);
    // Abort everything still running for the signed-out user.
    endpointsBootRef.current = null;
    bumpEndpointEpoch();
  }, []);

  const setActiveEndpoint = useCallback(
    (id: number) => {
      if (!session) return;
      // Stop impersonation when changing endpoints — user/operator/group overrides are endpoint-scoped.
      // Preserve DEBUG overrideIPAddress (global by design) so sign-in IP impersonation survives endpoint pick.
      const keepIP = __DEV_FEATURES__ && impersonationRef.current?.mode === "select"
        ? impersonationRef.current.overrideIPAddress
        : undefined;
      const nextImp: ImpersonationState | null = keepIP
        ? { mode: "select", overrideIPAddress: keepIP }
        : null;
      setImpersonationState(nextImp);
      impersonationRef.current = nextImp;
      const updated: Session = { ...session, ActiveEndPointID: id };
      persistSession(updated);
      applySession(updated);
      // Persist the newly-active endpoint so next reload has Title/ModuleID/etc. immediately.
      const activeEp = endpoints.find(e => e.ID === id) ?? null;
      persistActiveEndpoint(updated.UID, activeEp);
      pushRecentEndpointId(updated.UID, id);
      // Close the previous epoch before anything for the new endpoint goes out:
      // aborts requests still running against the old endpoint and flushes the
      // read-batch de-dup map, so this endpoint can't adopt the old one's data.
      endpointsBootRef.current = null;
      bumpEndpointEpoch();
      // Try to restore cached data for the new endpoint
      const stored = loadPersistedCache(updated.UID, id) as InitCache | null;
      if (stored && isCacheShapeValid(stored)) {
        parseMigratedFilters(stored);
        cacheRef.current = stored;
        setCache(stored);
      } else {
        if (stored) clearPersistedCache(updated.UID, id);
        cacheRef.current = null;
        setCache(null);
      }
      // Fetch delta (or full if no stored cache)
      fetchInitCache(updated).catch(logUnlessDropped);
    },
    [session, endpoints, fetchInitCache]
  );

  const switchEndpoint = useCallback(() => {
    if (!session) return;
    // Stop impersonation when switching endpoints — except preserve DEBUG overrideIPAddress (global).
    const keepIP = __DEV_FEATURES__ && impersonationRef.current?.mode === "select"
      ? impersonationRef.current.overrideIPAddress
      : undefined;
    const nextImp: ImpersonationState | null = keepIP
      ? { mode: "select", overrideIPAddress: keepIP }
      : null;
    setImpersonationState(nextImp);
    impersonationRef.current = nextImp;
    // Park the session on "no endpoint" FIRST. servicePost resolves the session
    // only after its first await, so anything started before this point would
    // read the zeroed value anyway — the old ordering bought nothing.
    const updated: Session = { ...session, ActiveEndPointID: 0 };
    persistSession(updated);
    applySession(updated);
    cacheRef.current = null;
    setCache(null);
    // No active endpoint → drop persisted copy.
    persistActiveEndpoint(updated.UID, null);
    // Then close the epoch: aborts everything still running against the endpoint
    // we just left, so a late continuation (e.g. cockpitApi's markAgentResultSeen)
    // can't fire a follow-up call that reaches the server with endpoint 0.
    endpointsBootRef.current = null;
    bumpEndpointEpoch();
    // Refetch endpoints so the picker reflects current IP-restriction state (including DEBUG override).
    fetchEndpoints().catch(logUnlessDropped);
  }, [session, fetchEndpoints]);

  const refreshCache = useCallback(async () => {
    if (!session || !session.ActiveEndPointID) return;
    await fetchInitCache(session);
  }, [session, fetchInitCache]);

  const clearCache = useCallback(async () => {
    if (!session || !session.ActiveEndPointID) return;
    // Manual clear is an explicit user action — reset the auto-clear rate-limit so the
    // next load isn't blocked by a previous broken-cache burst on this tab.
    resetCacheClearAttempts();
    cacheRef.current = null;
    setCache(null);
    clearPersistedCache(session.UID, session.ActiveEndPointID);
    await fetchInitCache(session);
  }, [session, fetchInitCache]);

  /* Append a single row to `cache.CommonData.Common` without a server round-
   * trip. Used by ComboBoxInput's onAddNew flow after `WebSaveValuesCB`
   * succeeds — we already have the canonical row, so we splice it locally
   * and persist, sparing a full WebInitCache fetch. The row should match
   * the loose CommonCacheItem shape (sName + sDisplayText + sFilterValue at
   * minimum); duplicates are caller's responsibility (the server-side
   * `WebSaveValuesCB` only succeeds for net-new entries). */
  const appendCommonValue = useCallback((item: unknown) => {
    setCache((prev) => {
      if (!prev) return prev;
      const next: InitCache = {
        ...prev,
        CommonData: {
          ...prev.CommonData,
          Common: [...prev.CommonData.Common, item],
        },
      };
      cacheRef.current = next;
      const s = sessionRef.current;
      if (s?.UID && s?.ActiveEndPointID) persistUnlessImpersonating(impersonationRef.current, s.UID, s.ActiveEndPointID, next);
      return next;
    });
  }, []);

  /* Generic upsert-by-key into a top-level or CommonData array. Mirrors the
   * delta-merge semantics in cacheMerge.ts (replace by primary key, else
   * append) so a locally-spliced row survives the next delta untouched. */
  const upsertKiosk = useCallback((item: KioskCacheItem) => {
    setCache((prev) => {
      if (!prev) return prev;
      const arr = prev.CommonData.Kiosk;
      const idx = arr.findIndex((r) => r.KioskID === item.KioskID);
      const merged = idx >= 0
        ? arr.map((r, i) => (i === idx ? item : r))
        : [...arr, item];
      const next: InitCache = {
        ...prev,
        CommonData: { ...prev.CommonData, Kiosk: merged },
      };
      cacheRef.current = next;
      const s = sessionRef.current;
      if (s?.UID && s?.ActiveEndPointID) persistUnlessImpersonating(impersonationRef.current, s.UID, s.ActiveEndPointID, next);
      return next;
    });
  }, []);

  const upsertLocation = useCallback((item: LocationCacheItem) => {
    setCache((prev) => {
      if (!prev) return prev;
      const arr = prev.CommonData.Location;
      const idx = arr.findIndex((r) => r.LocationID === item.LocationID);
      const merged = idx >= 0
        ? arr.map((r, i) => (i === idx ? item : r))
        : [...arr, item];
      const next: InitCache = {
        ...prev,
        CommonData: { ...prev.CommonData, Location: merged },
      };
      cacheRef.current = next;
      const s = sessionRef.current;
      if (s?.UID && s?.ActiveEndPointID) persistUnlessImpersonating(impersonationRef.current, s.UID, s.ActiveEndPointID, next);
      return next;
    });
  }, []);

  const upsertProduct = useCallback((item: ProductCacheItem) => {
    setCache((prev) => {
      if (!prev) return prev;
      const arr = prev.CommonData.Product;
      const idx = arr.findIndex((r) => r.ProductID === item.ProductID);
      const merged = idx >= 0
        ? arr.map((r, i) => (i === idx ? item : r))
        : [...arr, item];
      const next: InitCache = {
        ...prev,
        CommonData: { ...prev.CommonData, Product: merged },
      };
      cacheRef.current = next;
      const s = sessionRef.current;
      if (s?.UID && s?.ActiveEndPointID) persistUnlessImpersonating(impersonationRef.current, s.UID, s.ActiveEndPointID, next);
      return next;
    });
  }, []);

  const upsertIngressGroup = useCallback((item: { ID: number; [k: string]: unknown }) => {
    setCache((prev) => {
      if (!prev) return prev;
      const arr = (prev.CommonData.IngressGroup ?? []) as { ID?: number }[];
      const idx = arr.findIndex((r) => r?.ID === item.ID);
      const merged = idx >= 0
        ? arr.map((r, i) => (i === idx ? item : r))
        : [...arr, item];
      const next: InitCache = {
        ...prev,
        CommonData: { ...prev.CommonData, IngressGroup: merged },
      };
      cacheRef.current = next;
      const s = sessionRef.current;
      if (s?.UID && s?.ActiveEndPointID) persistUnlessImpersonating(impersonationRef.current, s.UID, s.ActiveEndPointID, next);
      return next;
    });
  }, []);

  const upsertPromotion = useCallback((item: unknown) => {
    setCache((prev) => {
      if (!prev) return prev;
      const id = (item as { PromotionID?: number })?.PromotionID;
      if (id == null) return prev;
      const arr = prev.Promotions;
      const idx = arr.findIndex((r) => (r as { PromotionID?: number })?.PromotionID === id);
      const merged = idx >= 0
        ? arr.map((r, i) => (i === idx ? item : r))
        : [...arr, item];
      const next: InitCache = { ...prev, Promotions: merged };
      cacheRef.current = next;
      const s = sessionRef.current;
      if (s?.UID && s?.ActiveEndPointID) persistUnlessImpersonating(impersonationRef.current, s.UID, s.ActiveEndPointID, next);
      return next;
    });
  }, []);

  const refreshEndpointsForIPImpersonation = useCallback(async () => {
    if (!session) return;
    const fresh = await fetchEndpoints();
    const stillValid = session.ActiveEndPointID === 0 ||
      fresh.some((ep) => ep.ID === session.ActiveEndPointID);
    if (!stillValid) {
      const updated: Session = { ...session, ActiveEndPointID: 0 };
      persistSession(updated);
      applySession(updated);
      setCache(null);
    }
  }, [session, fetchEndpoints]);

  const setImpersonation = useCallback(
    (state: ImpersonationState | null) => {
      setImpersonationState(state);
      impersonationRef.current = state;
      // Drop cache and refetch with new impersonation context
      if (session && session.ActiveEndPointID) {
        cacheRef.current = null;
        setCache(null);
        clearPersistedCache(session.UID, session.ActiveEndPointID);
        // fetchInitCache will pick up the new impersonation via the accessor
        fetchInitCache(session).catch(logUnlessDropped);
      }
    },
    [session, fetchInitCache]
  );

  const canRead = useCallback(
    (pageRowId: number): boolean => {
      if (!cache) return false;
      const perm = cache.Permissions.find((p) => p.RowID === pageRowId);
      return perm?.Read ?? false;
    },
    [cache]
  );

  const canWrite = useCallback(
    (pageRowId: number): boolean => {
      if (!cache) return false;
      const perm = cache.Permissions.find((p) => p.RowID === pageRowId);
      return perm?.Write ?? false;
    },
    [cache]
  );

  return (
    <AuthContext.Provider
      value={{ session, cache, loading, phase, endpoints, activeEndpoint, fetchEndpoints, login, logout, setActiveEndpoint, switchEndpoint, refreshCache, clearCache, appendCommonValue, upsertKiosk, upsertLocation, upsertPromotion, upsertProduct, upsertIngressGroup, debugRefetchWithTs, canRead, canWrite, impersonation, setImpersonation, refreshEndpointsForIPImpersonation, realSuperAdmin, realSuperUserTools, isSuperAdmin, effectiveUserId, clientOverride, setClientOverride }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
